"use client";

import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "motion/react";
import { format, isValid, parse, toDate } from "date-fns";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import * as z from "zod";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { CalendarWithYearSelect } from "@/components/ui/CalendarWithYear";
import { cn } from "@/lib/utils";
import { editTutorProfile } from "@/features/profile/tutor/api/editProfile.ts";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { InformationTooltip } from "@/components/InformationTooltip";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";

interface CertificatesEditProps {
  onSave: () => void;
}

enum ViewState {
  CERTIFICATES_LIST = "CERTIFICATES_LIST",
  ADD_EDIT_CERTIFICATE = "ADD_EDIT_CERTIFICATE",
}

interface CertificateItem {
  name: string;
  issuing_organization: string;
  issue_date?: string;
  expiration_date?: string;
  credential_id?: string;
  credential_url?: string;
  description?: string;
}

export function CertificatesEdit({ onSave }: CertificatesEditProps) {
  const { profileData, setToReload } = useProfileDataStore();
  const { t, i18n } = useTranslation(["profile", "common", "forms"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  const [currentView, setCurrentView] = useState<ViewState>(ViewState.CERTIFICATES_LIST);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [localCertificates, setLocalCertificates] = useState<CertificateItem[]>([]);
  const [isFormInitialized, setIsFormInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const setFooter = useProfileEditFooterStore((s) => s.setFooter);

  // Initialize local certificates state
  useEffect(() => {
    if (profileData?.certificates_section) {
      setLocalCertificates(
        profileData.certificates_section.map((cert) => ({
          name: cert.name ?? "",
          issuing_organization: cert.issuing_organization ?? "",
          issue_date: cert.issue_date ? cert.issue_date : undefined,
          expiration_date: cert.expiration_date ? cert.expiration_date : undefined,
          credential_url: cert.credential_url ?? "",
          credential_id: cert.credential_id ?? "",
          description: cert.description ?? "",
        })),
      );
    }
  }, [profileData]);

  const defaultValues = useMemo(() => {
    return {
      certificates: localCertificates.length > 0 ? localCertificates : [],
    };
  }, [localCertificates]);

  // Updated schema to allow empty certificates array
  const certificatesSectionSchema = useMemo(
    () =>
      z.object({
        certificates: z
          .array(
            z
              .object({
                name: z.string().min(3, t("forms:errors.certificateNameMin")).max(255),
                issuing_organization: z
                  .string()
                  .min(3, t("forms:errors.issuingOrganizationMin"))
                  .max(255),
                issue_date: z.string().optional(),
                expiration_date: z.string().optional(),
                credential_id: z.string().max(255).optional(),
                credential_url: z
                  .string()
                  .url(t("forms:errors.invalidUrl"))
                  .max(255)
                  .optional()
                  .or(z.literal("")),
                description: z.string().max(1000).optional(),
              })
              .refine(
                (data) => {
                  if (data.issue_date && data.expiration_date) {
                    return data.expiration_date >= data.issue_date;
                  }

                  return true;
                },
                {
                  message: t("forms:errors.endDateAfterStartDate"),
                  path: ["expiration_date"],
                },
              ),
          )
          .optional()
          .default([]),
      }),
    [t],
  );

  // Single certificate validation schema
  const singleCertificateSchema = useMemo(
    () =>
      z
        .object({
          name: z.string().min(3, t("forms:errors.certificateNameMin")).max(255),
          issuing_organization: z
            .string()
            .min(3, t("forms:errors.issuingOrganizationMin"))
            .max(255),
          issue_date: z.string().optional(),
          expiration_date: z.string().optional(),
          credential_id: z.string().max(255).optional(),
          credential_url: z
            .string()
            .url(t("forms:errors.invalidUrl"))
            .max(255)
            .optional()
            .or(z.literal("")),
          description: z.string().max(1000).optional(),
        })
        .refine(
          (data) => {
            if (data.issue_date && data.expiration_date) {
              return data.expiration_date >= data.issue_date;
            }

            return true;
          },
          {
            message: t("forms:errors.endDateAfterStartDate"),
            path: ["expiration_date"],
          },
        ),
    [t],
  );

  const form = useForm<{ certificates: CertificateItem[] }>({
    resolver: zodResolver(certificatesSectionSchema),
    defaultValues: async () => defaultValues,
  });

  const { update, replace } = useFieldArray({
    control: form.control,
    name: "certificates",
    shouldUnregister: true,
  });

  // Ensure the form's field array is initialized with the loaded certificates
  useEffect(() => {
    if (!isFormInitialized) {
      form.reset({ certificates: localCertificates });
      setIsFormInitialized(true);
    }
  }, [localCertificates, isFormInitialized]);

  // Single certificate form for add/edit
  const singleCertificateForm = useForm<CertificateItem>({
    resolver: zodResolver(singleCertificateSchema),
    defaultValues: {
      name: "",
      issuing_organization: "",
      issue_date: undefined,
      expiration_date: undefined,
      credential_id: "",
      credential_url: "",
      description: "",
    },
    mode: "onChange",
  });

  const EMPTY_CERTIFICATE_ENTRY: CertificateItem = {
    name: "",
    issuing_organization: "",
    issue_date: undefined,
    expiration_date: undefined,
    credential_id: "",
    credential_url: "",
    description: "",
  };

  const parseDate = (dateString: string | Date | undefined): Date | undefined => {
    if (!dateString) return undefined;
    if (dateString instanceof Date) return dateString;
    let date = new Date(dateString);

    if (isValid(date)) return date;
    const formats = ["dd.MM.yyyy", "dd-MM-yyyy", "yyyy-MM-dd", "MM/dd/yyyy", "dd/MM/yyyy"];

    for (const formatString of formats) {
      date = parse(dateString, formatString, new Date());
      if (isValid(date)) return date;
    }
    console.warn(`Unable to parse date: ${dateString}`);

    return undefined;
  };

  const formatDateForAPI = (date: Date | string | undefined): string | undefined => {
    if (!date) return undefined;
    const parsedDate = parseDate(date);

    return parsedDate ? format(parsedDate, "yyyy-MM-dd") : undefined;
  };

  // Function to format date range for display
  const formatDateRange = useCallback(
    (issueDate?: Date | string, expirationDate?: Date | string) => {
      if (!issueDate) return "";

      const issue = format(parseDate(issueDate) ?? "", "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      if (!expirationDate) {
        return `${issue}`;
      }

      const expiration = format(parseDate(expirationDate) ?? "", "MMM yyyy", {
        locale: getDateFnsLocale(i18n.language),
      });

      return `${issue} - ${expiration}`;
    },
    [i18n.language],
  );

  const handleAddCertificate = useCallback(
    (data: CertificateItem) => {
      const result = singleCertificateSchema.safeParse(data);

      if (result.success) {
        const formattedData = {
          ...result.data,
          issue_date: formatDateForAPI(result.data.issue_date),
          expiration_date: formatDateForAPI(result.data.expiration_date),
        };

        if (editingIndex !== null) {
          // Update existing certificate
          const updatedCertificates = [...localCertificates];

          updatedCertificates[editingIndex] = formattedData;
          setLocalCertificates(updatedCertificates);
          update(editingIndex, formattedData);
        } else {
          // Add new certificate, keep RHF array in sync with full list
          const nextCertificates = [...localCertificates, formattedData];

          setLocalCertificates(nextCertificates);
          replace(nextCertificates);
        }

        singleCertificateForm.reset(EMPTY_CERTIFICATE_ENTRY);
        setEditingIndex(null);
        setCurrentView(ViewState.CERTIFICATES_LIST);

        return true;
      } else {
        // Set errors on the form
        Object.keys(result.error.flatten().fieldErrors).forEach((fieldName) => {
          singleCertificateForm.trigger(fieldName as keyof CertificateItem);
        });

        return false;
      }
    },
    [
      editingIndex,
      localCertificates,
      update,
      replace,
      singleCertificateForm,
      singleCertificateSchema,
    ],
  );

  const handleEditCertificate = useCallback(
    (index: number) => {
      const certificate = localCertificates[index];

      setEditingIndex(index);
      singleCertificateForm.reset(certificate);
      setCurrentView(ViewState.ADD_EDIT_CERTIFICATE);
    },
    [localCertificates, singleCertificateForm],
  );

  const handleRemoveCertificate = useCallback(
    (index: number) => {
      const updatedCertificates = localCertificates.filter((_, i) => i !== index);

      setLocalCertificates(updatedCertificates);
      // Keep RHF field array in sync with the full list
      replace(updatedCertificates);
    },
    [localCertificates, replace],
  );

  const submitAllCertificates = async () => {
    try {
      setIsSaving(true);
      if (profileData) {
        const formatted = localCertificates.map((cert) => ({
          ...cert,
          issue_date: formatDateForAPI(cert.issue_date),
          expiration_date: formatDateForAPI(cert.expiration_date),
        }));

        await editTutorProfile(profileData.id, "certificates", { certificates: formatted });
        toast.success(t("common:success"), {
          description: t("common:successMessage"),
        });
        onSave();
        setToReload(true);
      }
    } catch (error: any) {
      console.error(error);
      const errors = error.response?.data?.errors;

      if (errors) {
        Object.entries(errors).forEach(([key, values]) => {
          (values as string[]).forEach((value: string) => {
            form.setError(key as keyof typeof form.formState.errors, { message: value });
          });
        });
      }
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Animation variants
  const stepVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
        },
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.2,
        },
      },
    }),
    [],
  );

  const cardVariants = useMemo(
    () => ({
      hidden: { opacity: 0, scale: 0.95 },
      visible: (i: number) => ({
        opacity: 1,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30,
          delay: i * 0.1,
        },
      }),
      exit: {
        opacity: 0,
        scale: 0.95,
        transition: {
          duration: 0.2,
          ease: "easeInOut",
        },
      },
    }),
    [],
  );

  // Certificates List View
  const renderCertificatesListView = () => (
    <motion.div
      animate="visible"
      className="flex h-full flex-col"
      exit="exit"
      initial="hidden"
      variants={stepVariants}
    >
      <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 opacity-10" />
        <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 max-sm:flex-col">
            <div className="flex items-center gap-2">
              <div className="bg-background/60 flex items-center rounded-xl p-2">
                <Icon
                  className="text-accent-foreground"
                  height="1.125rem"
                  icon="mdi:certificate-outline"
                  width="1.125rem"
                />
              </div>
              <CardTitle className="text-accent-foreground text-lg">
                {t("profile:certificatesSection.cards.list.title", {
                  defaultValue: "Certificates",
                })}
              </CardTitle>
              <InformationTooltip
                description={t("profile:certificatesSection.tooltips.list", {
                  defaultValue: "Add certificates and credentials.",
                })}
              />
            </div>
            <Button
              className="h-9 px-3"
              size="sm"
              type="button"
              variant="foreground"
              onClick={() => {
                setEditingIndex(null);
                singleCertificateForm.reset(EMPTY_CERTIFICATE_ENTRY);
                setCurrentView(ViewState.ADD_EDIT_CERTIFICATE);
              }}
            >
              <Icon className="mr-2" height="1rem" icon="mdi:plus" width="1rem" />
              {t("forms:certificates.addCertificate")}
            </Button>
          </div>
          <CardDescription className="text-muted-foreground">
            {t("profile:certificatesSection.cards.list.description", {
              defaultValue: "Manage your certificates and credentials",
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-24">
          <div className="space-y-4 pb-2">
            <AnimatePresence mode="popLayout">
              {localCertificates.map((certificate, index) => (
                <motion.div
                  key={index}
                  layout
                  animate="visible"
                  custom={index}
                  exit="exit"
                  initial="hidden"
                  variants={cardVariants}
                >
                  <Card className="bg-muted/60 border-border/30 hover:border-accent/30 hover:bg-accent/50 cursor-pointer transition-all">
                    <CardContent className="flex items-start justify-between gap-4 p-5">
                      <div
                        className="flex min-w-0 flex-1 items-start gap-4"
                        onClick={() => handleEditCertificate(index)}
                      >
                        <div className="bg-background/60 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full max-[350px]:hidden">
                          <Icon
                            className="text-accent-foreground"
                            height="1.125rem"
                            icon="mdi:certificate"
                            width="1.125rem"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-accent-foreground mb-1 truncate text-base font-semibold">
                            {certificate.name}
                          </h4>
                          <div className="space-y-1">
                            {certificate.issuing_organization && (
                              <p className="text-muted-foreground text-sm font-medium">
                                {certificate.issuing_organization}
                              </p>
                            )}
                            {(certificate.issue_date || certificate.expiration_date) && (
                              <p className="text-muted-foreground text-xs font-medium">
                                {formatDateRange(
                                  certificate.issue_date,
                                  certificate.expiration_date,
                                )}
                              </p>
                            )}
                            {certificate.description && (
                              <p className="text-muted-foreground mt-2 line-clamp-2 text-sm">
                                {certificate.description}
                              </p>
                            )}
                            {certificate.credential_id && (
                              <div className="mt-1 flex items-center text-xs">
                                <Icon
                                  className="text-accent-foreground mr-1.5"
                                  height="0.875rem"
                                  icon="mdi:identifier"
                                  width="0.875rem"
                                />
                                <span className="text-muted-foreground line-clamp-1">
                                  {certificate.credential_id}
                                </span>
                              </div>
                            )}
                            {certificate.credential_url && (
                              <div className="mt-1 flex items-center text-xs">
                                <Icon
                                  className="text-accent-foreground mr-1.5"
                                  height="0.875rem"
                                  icon="mdi:link-variant"
                                  width="0.875rem"
                                />
                                <a
                                  className="text-accent-foreground hover:text-accent-foreground/80 truncate underline"
                                  href={certificate.credential_url}
                                  rel="noopener noreferrer"
                                  target="_blank"
                                >
                                  {t("forms:certificates.viewCredential")}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive flex-shrink-0"
                        size="sm"
                        type="button"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCertificate(index);
                        }}
                      >
                        <Icon height="1rem" icon="mdi:trash-outline" width="1rem" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>

            {localCertificates.length === 0 && (
              <div className="flex items-center justify-center py-8 text-center">
                <div className="text-muted-foreground">
                  <Icon
                    className="mx-auto mb-3"
                    height="3rem"
                    icon="mdi:certificate-outline"
                    width="3rem"
                  />
                  <p className="mb-1 text-base font-medium">
                    {t("forms:certificates.noCertificates")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  // Add/Edit Certificate View
  const renderAddEditCertificateView = () => (
    <motion.div
      animate="visible"
      className="flex h-full flex-col"
      exit="exit"
      initial="hidden"
      variants={stepVariants}
    >
      <div className="min-h-0 flex-1">
        <div className="space-y-4 pb-2">
          {/* Certificate & Timeline */}
          <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 opacity-10" />
            <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-tr from-emerald-400 to-teal-500 opacity-10" />
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="bg-background/60 flex items-center rounded-xl p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1.125rem"
                    icon="mdi:certificate"
                    width="1.125rem"
                  />
                </div>
                <CardTitle className="text-accent-foreground text-lg">
                  {t("forms:certificates.certificateDetails")}
                </CardTitle>
                <InformationTooltip
                  description={t("profile:certificatesSection.tooltips.certificateDetails", {
                    defaultValue: "Provide certificate details and timeline.",
                  })}
                />
              </div>
            </CardHeader>
            <CardContent className="pb-24">
              <div className="space-y-4">
                {/* Certificate name */}
                <FormField
                  control={singleCertificateForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                        <Icon height="1rem" icon="mdi:certificate" width="1rem" />
                        {t("forms:certificates.certificateName")}
                      </div>
                      <FormControl>
                        <div className="relative">
                          <Input
                            className="h-12 pr-12 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:certificates.certificateNamePlaceholder")}
                            {...field}
                          />
                          <div className="absolute inset-y-0 right-4 flex items-center">
                            <Icon
                              className={cn(
                                "transition-colors",
                                field.value.length >= 3
                                  ? "text-accent-foreground"
                                  : "text-muted-foreground",
                              )}
                              height="1rem"
                              icon={
                                field.value.length >= 3
                                  ? "mdi:check-circle"
                                  : "mdi:close-circle-outline"
                              }
                              width="1rem"
                            />
                          </div>
                        </div>
                      </FormControl>
                      <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                        <ErrorMessage errors={singleCertificateForm.formState.errors} name="name" />
                      </p>
                    </FormItem>
                  )}
                />

                {/* Issuing organization */}
                <FormField
                  control={singleCertificateForm.control}
                  name="issuing_organization"
                  render={({ field }) => (
                    <FormItem>
                      <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                        <Icon height="1rem" icon="mdi:domain" width="1rem" />
                        {t("forms:certificates.issuingOrganization")}
                      </div>
                      <FormControl>
                        <div className="relative">
                          <Input
                            className="h-12 pr-12 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:certificates.issuingOrgPlaceholder")}
                            {...field}
                          />
                          <div className="absolute inset-y-0 right-4 flex items-center">
                            <Icon
                              className={cn(
                                "transition-colors",
                                field.value.length >= 3
                                  ? "text-accent-foreground"
                                  : "text-muted-foreground",
                              )}
                              height="1rem"
                              icon={
                                field.value.length >= 3
                                  ? "mdi:check-circle"
                                  : "mdi:close-circle-outline"
                              }
                              width="1rem"
                            />
                          </div>
                        </div>
                      </FormControl>
                      <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                        <ErrorMessage
                          errors={singleCertificateForm.formState.errors}
                          name="issuing_organization"
                        />
                      </p>
                    </FormItem>
                  )}
                />

                {/* Timeline */}
                <div>
                  <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                    <Icon height="1rem" icon="mdi:timeline-clock" width="1rem" />
                    {t("forms:certificates.timeline")}
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormField
                      control={singleCertificateForm.control}
                      name="issue_date"
                      render={({ field }) => (
                        <FormItem>
                          <div className="text-muted-foreground mb-1 text-xs font-medium">
                            {t("forms:certificates.issueDate")}
                          </div>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  className={cn(
                                    "border-input bg-background h-12 w-full justify-start border px-4 text-left text-sm font-normal transition-all focus-visible:ring-2 focus-visible:ring-offset-1",
                                    !field.value && "text-muted-foreground",
                                  )}
                                  variant="outline"
                                >
                                  <Icon
                                    className="mr-3"
                                    height="1rem"
                                    icon="mdi:calendar-outline"
                                    width="1rem"
                                  />
                                  {field.value ? (
                                    <span className="text-accent-foreground">
                                      {format(new Date(field.value), "MMM yyyy", {
                                        locale: getDateFnsLocale(i18n.language),
                                      })}
                                    </span>
                                  ) : (
                                    <span>{t("forms:certificates.selectDate")}</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="z-[5000] w-auto p-0">
                              <CalendarWithYearSelect
                                initialFocus
                                disabled={(date) => date > new Date()}
                                fromYearSelect={new Date().getFullYear() - 49}
                                language={i18n.language}
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                weekStartsOn={1}
                                onSelect={(date) =>
                                  field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                }
                              />
                            </PopoverContent>
                          </Popover>
                          <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                            <ErrorMessage
                              errors={singleCertificateForm.formState.errors}
                              name="issue_date"
                            />
                          </p>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={singleCertificateForm.control}
                      name="expiration_date"
                      render={({ field }) => (
                        <FormItem>
                          <div className="text-muted-foreground mb-1 text-xs font-medium">
                            {t("forms:certificates.expirationDate")}
                          </div>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  className={cn(
                                    "border-input bg-background h-12 w-full justify-start border px-4 text-left text-sm font-normal transition-all focus-visible:ring-2 focus-visible:ring-offset-1",
                                    !field.value && "text-muted-foreground",
                                    !singleCertificateForm.watch("issue_date") && "opacity-50",
                                  )}
                                  disabled={!singleCertificateForm.watch("issue_date")}
                                  variant="outline"
                                >
                                  <Icon
                                    className="mr-3"
                                    height="1rem"
                                    icon="mdi:calendar-outline"
                                    width="1rem"
                                  />
                                  {field.value ? (
                                    <span className="text-accent-foreground">
                                      {format(new Date(field.value), "MMM yyyy", {
                                        locale: getDateFnsLocale(i18n.language),
                                      })}
                                    </span>
                                  ) : (
                                    <span>{t("forms:certificates.selectDate")}</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="z-[5000] w-auto p-0">
                              <CalendarWithYearSelect
                                initialFocus
                                disabled={(date) => {
                                  const startDate = singleCertificateForm.watch("issue_date");

                                  return startDate ? date < toDate(startDate) : false;
                                }}
                                fromYearSelect={new Date().getFullYear() - 49}
                                language={i18n.language}
                                mode="single"
                                selected={field.value ? new Date(field.value) : undefined}
                                weekStartsOn={1}
                                onSelect={(date) =>
                                  field.onChange(format(date ?? new Date(), "yyyy-MM-dd"))
                                }
                              />
                            </PopoverContent>
                          </Popover>
                          <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                            <ErrorMessage
                              errors={singleCertificateForm.formState.errors}
                              name="expiration_date"
                            />
                          </p>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional info & Description */}
          <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 opacity-10" />
            <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-10" />
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="bg-background/60 flex items-center rounded-xl p-2">
                  <Icon
                    className="text-accent-foreground"
                    height="1.125rem"
                    icon="mdi:file-document"
                    width="1.125rem"
                  />
                </div>
                <CardTitle className="text-accent-foreground text-lg">
                  {t("forms:certificates.additionalInfo")}
                </CardTitle>
                <InformationTooltip
                  description={t("profile:certificatesSection.tooltips.additionalInfo", {
                    defaultValue: "Add credential identifiers and links.",
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="space-y-4">
                  <FormField
                    control={singleCertificateForm.control}
                    name="credential_id"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:identifier"
                            width="1rem"
                          />
                          {t("forms:certificates.credentialId")}
                        </div>
                        <FormControl>
                          <Input
                            className="h-12 pr-4 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:certificates.credentialIdPlaceholder")}
                            {...field}
                          />
                        </FormControl>
                        <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                          <ErrorMessage
                            errors={singleCertificateForm.formState.errors}
                            name="credential_id"
                          />
                        </p>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={singleCertificateForm.control}
                    name="credential_url"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:link-variant"
                            width="1rem"
                          />
                          {t("forms:certificates.credentialUrl")}
                        </div>
                        <FormControl>
                          <Input
                            className="h-12 pr-4 pl-4 text-base transition-all focus-visible:ring-2 focus-visible:ring-offset-1"
                            placeholder={t("forms:certificates.credentialUrlPlaceholder")}
                            type="url"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                          <ErrorMessage
                            errors={singleCertificateForm.formState.errors}
                            name="credential_url"
                          />
                        </p>
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <FormField
                    control={singleCertificateForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:text"
                            width="1rem"
                          />
                          {t("forms:certificates.description")}
                        </div>
                        <FormControl>
                          <div className="relative">
                            <Textarea
                              className="border-input [field-sizing:content] min-h-[170px] resize-none rounded-xl border p-4 text-base focus-visible:ring-2 focus-visible:ring-offset-1"
                              placeholder={t("forms:education.descriptionPlaceholder")}
                              {...field}
                            />
                            <div className="text-muted-foreground absolute right-4 bottom-3 text-xs">
                              {field.value?.length || 0}/1000
                            </div>
                          </div>
                        </FormControl>
                        <p className="text-destructive mt-2 min-h-[1.25rem] text-sm">
                          <ErrorMessage
                            errors={singleCertificateForm.formState.errors}
                            name="description"
                          />
                        </p>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer handled by persistent footer */}
    </motion.div>
  );

  // Register persistent footer content for this section
  useEffect(() => {
    const footerNode =
      currentView === ViewState.CERTIFICATES_LIST ? (
        <div className="flex w-full justify-end gap-3">
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            variant="outline"
            onClick={onSave}
          >
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
            disabled={isSaving}
            type="button"
            onClick={submitAllCertificates}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:check-circle" width="1rem" />
            {isSaving ? t("common:saving") : t("common:save")}
          </Button>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between gap-3">
          <Button
            className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
            type="button"
            variant="outline"
            onClick={() => {
              singleCertificateForm.reset(EMPTY_CERTIFICATE_ENTRY);
              setEditingIndex(null);
              setCurrentView(ViewState.CERTIFICATES_LIST);
            }}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:arrow-left" width="1rem" />
            {t("common:cancel")}
          </Button>
          <Button
            className="bg-accent-foreground/90 hover:bg-accent-foreground text-background"
            type="button"
            onClick={() => handleAddCertificate(singleCertificateForm.getValues())}
          >
            <Icon className="mr-2" height="1rem" icon="mdi:check-circle" width="1rem" />
            {t("common:save")}
          </Button>
        </div>
      );

    setFooter({ key: `certificates-${currentView}`, node: footerNode });

    return () => {
      // Keep footer stable across quick view changes
    };
  }, [
    currentView,
    isSaving,
    t,
    onSave,
    submitAllCertificates,
    singleCertificateForm,
    setEditingIndex,
  ]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <form
          className="flex h-full flex-col pb-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitAllCertificates();
          }}
        >
          <div className="flex h-full flex-col">
            <AnimatePresence mode="wait">
              {currentView === ViewState.CERTIFICATES_LIST ? (
                <motion.div
                  key="certificates-list"
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  initial={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderCertificatesListView()}
                </motion.div>
              ) : (
                <motion.div
                  key="certificates-add-edit"
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  initial={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderAddEditCertificateView()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
